import { Controller } from "@hotwired/stimulus";
import { renderMarkdown } from "../lib/markdown.js";

function esc(s) {
  return String(s)
    .replace(/&/g, "&amp;").replace(/</g, "&lt;")
    .replace(/>/g, "&gt;").replace(/"/g, "&quot;");
}

function unwrapInput(raw) {
  try {
    const p = JSON.parse(raw);
    if (p && typeof p.input === "string") return p.input;
  } catch (e) {}
  return raw;
}

function fmtTokens(n) {
  const v = Number(n) || 0;
  if (v < 1000) return String(v);
  if (v < 1_000_000) return (v / 1000).toFixed(1) + "k";
  return (v / 1_000_000).toFixed(2) + "M";
}

function fmtCost(v) {
  const n = Number(v) || 0;
  return n < 0.01 ? "$" + n.toFixed(4) : "$" + n.toFixed(2);
}

// The meta strip and the right rail are addressed by id (server-rendered in
// run.html) rather than Stimulus targets, so a template can add a readout
// without also declaring a target for every span.
function setElText(id, val) {
  const el = document.getElementById(id);
  if (el) el.textContent = val;
}

function showEl(id) {
  const el = document.getElementById(id);
  if (el) el.style.display = "";
}

// Chevron-right SVG used in every disclosure summary; CSS in input.css
// rotates the [data-arrow] wrapper 90deg when the parent <details> is open.
const CHEVRON_SVG = '<svg class="w-3 h-3" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true"><path fill-rule="evenodd" d="M7.21 5.21a.75.75 0 0 1 1.06 0l4.25 4.25a.75.75 0 0 1 0 1.06l-4.25 4.25a.75.75 0 1 1-1.06-1.06L10.94 10 7.21 6.27a.75.75 0 0 1 0-1.06Z" clip-rule="evenodd"/></svg>';

const STATUS_CLASSES = {
  running:   "bg-blue-100 dark:bg-blue-900/50 text-blue-700 dark:text-blue-300 border border-blue-300 dark:border-blue-800",
  completed: "bg-green-100 dark:bg-green-900/50 text-green-700 dark:text-green-300 border border-green-300 dark:border-green-800",
  failed:    "bg-red-100 dark:bg-red-900/50 text-red-700 dark:text-red-300 border border-red-300 dark:border-red-800",
};

export default class extends Controller {
  static targets = ["feed", "statusBadge", "rerunForm", "haltForm", "structuredBox", "structuredPre", "liveIndicator", "waiting"];
  static values = {
    runId:  String,
    wsPath: String,
    status: String,
  };

  connect() {
    this.currentAssistantEl = null;
    this.currentAssistantRaw = "";
    this.currentToolRefs = null;
    this.reconnectDelay = 1000;
    // Defensive: if Stimulus ever re-connects this controller instance,
    // the prior disconnect() left `closing = true`. Reset it so the new
    // socket can reconnect normally on transient close events.
    this.closing = false;

    if (this.statusValue !== "running" && this.statusValue !== "pending") return;

    if (this.hasLiveIndicatorTarget) this.liveIndicatorTarget.style.display = "inline-block";

    const proto = location.protocol === "https:" ? "wss:" : "ws:";
    this.wsURL = `${proto}//${location.host}${this.wsPathValue}`;
    this.openSocket();
  }

  disconnect() {
    this.closing = true;
    if (this.ws) {
      try { this.ws.close(1000); } catch (e) {}
      this.ws = null;
    }
    if (this.reconnectTimer) clearTimeout(this.reconnectTimer);
  }

  // ── Auto-scroll ───────────────────────────────────────────────────────────
  //
  // Standard chat affordance: when a new message lands and the user was
  // already at the bottom of the page, keep them pinned there. If they
  // scrolled up to re-read context, don't yank them away.
  //
  // The "at bottom" check is captured synchronously at the top of every
  // handleMessage call — before any DOM mutation — and acted on after
  // the switch returns. A scroll-event-based stickToBottom flag would
  // race with our own programmatic scrollTo (the scroll event fires
  // asynchronously, so a WS message that arrives in the same tick
  // sees a stale flag).
  isAtBottom() {
    const doc = document.documentElement;
    return (window.innerHeight + window.scrollY) >= (doc.scrollHeight - 40);
  }

  scrollToBottom() {
    // Double rAF so the scroll fires after the browser has measured
    // the new content height. scrollTo with a stale scrollHeight
    // would land short of the bottom and lose stickiness; a single
    // rAF is sometimes enough but the second one is free insurance
    // against layout-batching surprises.
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        window.scrollTo({ top: document.documentElement.scrollHeight, behavior: "auto" });
      });
    });
  }

  openSocket() {
    // Each socket begins with a full DB catch-up, so the feed and the derived
    // rail counters must restart from zero or a reconnect would double them.
    // Clearing the feed also drops any per-turn DOM state: without the reset,
    // a reconnect mid-assistant-turn would keep appending into nodes the
    // clear detached.
    if (this.hasFeedTarget) this.feedTarget.innerHTML = "";
    this.currentAssistantEl = null;
    this.currentAssistantRaw = "";
    this.currentToolRefs = null;
    this.iterationCount = 0;
    this.toolCounts = new Map();

    const ws = new WebSocket(this.wsURL);
    this.ws = ws;

    ws.onmessage = (evt) => {
      let msg;
      try { msg = JSON.parse(evt.data); } catch (e) { return; }
      const p = msg.payload || {};
      this.handleMessage(msg.type, p, ws);
    };

    ws.onclose = (evt) => {
      if (this.closing || evt.code === 1000) return;
      this.reconnectTimer = setTimeout(() => this.openSocket(), this.reconnectDelay);
      this.reconnectDelay = Math.min(this.reconnectDelay * 2, 16000);
    };
  }

  handleMessage(type, p, ws) {
    // Capture before any mutation so the post-message scrollToBottom
    // only fires when the user was actually pinned to the bottom.
    const wasAtBottom = this.isAtBottom();
    switch (type) {
      case "running":
        this.setStatus("running", STATUS_CLASSES.running);
        break;
      case "chunk":
        this.removeWaiting();
        this.appendToAssistant(p.text || "");
        break;
      case "assistant_message":
        this.removeWaiting();
        if (!this.currentAssistantEl) this.startAssistantTurn();
        this.currentAssistantRaw = p.text || "";
        this.finalizeAssistant();
        this.bumpIterations();
        break;
      case "reasoning_chunk":
        this.removeWaiting();
        this.appendToReasoning(p.text || "");
        break;
      case "reasoning_message":
        this.removeWaiting();
        // The block was already built incrementally by reasoning_chunk on a
        // streaming provider; replace its text so a reconnecting client that
        // missed the transient chunks still gets the whole thing.
        this.setReasoning(p.text || "");
        break;
      case "system_message":
        this.removeWaiting();
        this.addSystemMessage(p.content || "");
        break;
      case "user_message":
        this.removeWaiting();
        this.addUserMessage(p.content || "");
        break;
      case "tool_call":
        this.removeWaiting();
        this.currentToolRefs = this.addToolCall(p.name || "", p.input || "");
        this.bumpToolCount(p.name || "");
        break;
      case "tool_result":
        this.setToolResult(this.currentToolRefs, p.output || "");
        break;
      case "citations":
        this.removeWaiting();
        this.addCitations(p.content || "");
        break;
      case "usage":
        this.applyUsage(p);
        break;
      case "complete":
        this.setStatus("completed", STATUS_CLASSES.completed);
        this.markCompletedNow();
        if (this.hasLiveIndicatorTarget) this.liveIndicatorTarget.style.display = "none";
        if (this.hasRerunFormTarget) this.rerunFormTarget.style.display = "";
        if (this.hasHaltFormTarget) this.haltFormTarget.style.display = "none";
        if (p.structured && this.hasStructuredBoxTarget) {
          try {
            this.structuredPreTarget.textContent = JSON.stringify(JSON.parse(p.structured), null, 2);
            this.structuredBoxTarget.style.display = "";
          } catch (e) {}
        }
        this.closing = true;
        ws.close(1000);
        break;
      case "failed":
        this.setStatus("failed", STATUS_CLASSES.failed);
        this.markCompletedNow();
        if (this.hasLiveIndicatorTarget) this.liveIndicatorTarget.style.display = "none";
        if (this.hasRerunFormTarget) this.rerunFormTarget.style.display = "";
        if (this.hasHaltFormTarget) this.haltFormTarget.style.display = "none";
        if (p.reason) this.addErrorTurn(p.reason);
        this.closing = true;
        ws.close(1000);
        break;
    }
    if (wasAtBottom) this.scrollToBottom();
  }

  // ── Chat rendering ────────────────────────────────────────────────────────

  // Helper to wrap any body content in a .turn row with avatar + role label
  makeTurn(role, avatarText, avatarBg, avatarFg) {
    const turn = document.createElement("div");
    turn.className = "turn";
    const avatar = document.createElement("div");
    avatar.className = "avatar";
    avatar.style.background = avatarBg;
    avatar.style.color = avatarFg;
    avatar.textContent = avatarText;
    const body = document.createElement("div");
    body.className = "body";
    const roleEl = document.createElement("div");
    roleEl.className = "role";
    roleEl.textContent = role;
    body.appendChild(roleEl);
    turn.appendChild(avatar);
    turn.appendChild(body);
    return { turn, body };
  }

  // ── Reasoning turn ────────────────────────────────────────────────────────
  // Mirrors the SSR shape in run.html so a live reasoning block looks
  // identical to one loaded from the DB. Built lazily on the first chunk and
  // reset by finalizeReasoning() so each model turn gets its own block.

  startReasoningTurn() {
    if (!this.hasFeedTarget) return null;
    const { turn, body } = this.makeTurn("Reasoning", "R", "var(--surface-2)", "var(--muted)");
    const avatar = turn.querySelector(".avatar");
    if (avatar) avatar.classList.add("reasoning-avatar");
    const d = document.createElement("details");
    d.className = "reasoning-block";
    d.open = true; // open while streaming so the user can watch it think
    const s = document.createElement("summary");
    s.innerHTML =
      '<svg class="caret w-3 h-3" fill="none" stroke="currentColor" stroke-width="2.5" viewBox="0 0 24 24">' +
      '<path stroke-linecap="round" stroke-linejoin="round" d="M9 5l7 7-7 7"/></svg><span>Thinking…</span>';
    const pre = document.createElement("pre");
    d.appendChild(s);
    d.appendChild(pre);
    body.appendChild(d);
    this.feedTarget.appendChild(turn);
    this.currentReasoningPre = pre;
    this.currentReasoningDetails = d;
    return pre;
  }

  appendToReasoning(text) {
    if (!text) return;
    // A reasoning delta after the assistant text means a new model turn has
    // started; close out the previous block first.
    if (this.currentAssistantEl) this.finalizeAssistant();
    if (!this.currentReasoningPre) this.startReasoningTurn();
    if (this.currentReasoningPre) this.currentReasoningPre.textContent += text;
  }

  setReasoning(text) {
    if (!text) return;
    if (this.currentAssistantEl) this.finalizeAssistant();
    if (!this.currentReasoningPre) this.startReasoningTurn();
    if (this.currentReasoningPre) this.currentReasoningPre.textContent = text;
    this.finalizeReasoning();
  }

  // Collapse the finished block and label it, so a long run doesn't leave
  // every turn's thinking expanded.
  finalizeReasoning() {
    if (!this.currentReasoningPre) return;
    const d = this.currentReasoningDetails;
    if (d) {
      d.open = false;
      const label = d.querySelector("summary span");
      if (label) label.textContent = "View thinking";
    }
    this.currentReasoningPre = null;
    this.currentReasoningDetails = null;
  }

  addSystemMessage(text) {
    if (!text || !text.trim()) return;
    const { turn, body } = this.makeTurn("System", "S", "var(--surface-2)", "var(--dim)");
    const d = document.createElement("details");
    d.className = "prompt-block";
    const s = document.createElement("summary");
    s.innerHTML = '<span>View system prompt</span><span style="color: var(--dim);">▸</span>';
    const pre = document.createElement("pre");
    pre.textContent = text;
    d.appendChild(s); d.appendChild(pre);
    body.appendChild(d);
    this.feedTarget.appendChild(turn);
  }

  // ── Web-search turn ───────────────────────────────────────────────────────
  // Mirrors the SSR shape in run.html. The payload is the JSON the runner
  // persisted, so both paths start from the same record rather than from two
  // descriptions of it.
  //
  // Everything here is set as text and the href is scheme-checked, because a
  // citation is the least trusted string on this page: the title is whatever
  // an arbitrary web page called itself, not even model output.
  addCitations(content) {
    if (!this.hasFeedTarget || !content) return;
    let rec;
    try {
      rec = JSON.parse(content);
    } catch {
      return;
    }
    if (!rec) return;
    const sources = Array.isArray(rec.sources) ? rec.sources : [];
    const searches = Number(rec.searches) || (sources.length ? 1 : 0);
    if (!searches && !sources.length) return;

    this.finalizeReasoning();
    this.finalizeAssistant();
    const { turn, body } = this.makeTurn("Web search", "W", "var(--surface-2)", "var(--muted)");
    const avatar = turn.querySelector(".avatar");
    if (avatar) avatar.classList.add("citation-avatar");

    const box = document.createElement("div");
    box.className = "citation-block";
    const count = document.createElement("div");
    count.className = "citation-count";
    count.textContent = `${searches} web ${searches === 1 ? "search" : "searches"}`;
    box.appendChild(count);

    if (sources.length) {
      const list = document.createElement("ul");
      list.className = "citation-sources";
      for (const src of sources) {
        const url = typeof src?.url === "string" ? src.url : "";
        if (!url) continue;
        const li = document.createElement("li");
        // A link is only a link when it goes somewhere a browser should
        // follow; anything else stays inert text.
        const safe = /^https?:\/\//i.test(url);
        const head = document.createElement(safe ? "a" : "span");
        if (safe) {
          head.href = url;
          head.target = "_blank";
          head.rel = "noopener noreferrer nofollow";
        }
        head.textContent = src?.title || url;
        const sub = document.createElement("span");
        sub.className = "citation-url";
        sub.textContent = url;
        li.appendChild(head);
        li.appendChild(sub);
        list.appendChild(li);
      }
      box.appendChild(list);
    } else {
      const none = document.createElement("div");
      none.style.marginTop = "4px";
      none.textContent = "no sources cited";
      box.appendChild(none);
    }

    body.appendChild(box);
    this.feedTarget.appendChild(turn);
  }

  // Append a red Error turn to the feed. Matches the SSR-rendered
  // shape in run.html so a live failure looks identical to a failed
  // run loaded fresh from the DB.
  addErrorTurn(reason) {
    if (!this.hasFeedTarget) return;
    this.finalizeAssistant();
    const { turn, body } = this.makeTurn("Error", "!", "var(--crit-soft-bg)", "var(--crit-soft-fg)");
    const role = body.querySelector(".role");
    if (role) role.style.color = "var(--crit-soft-fg)";
    const pre = document.createElement("pre");
    pre.className = "error-text";
    pre.textContent = reason;
    body.appendChild(pre);
    this.feedTarget.appendChild(turn);
  }

  addUserMessage(text) {
    if (!text || !text.trim()) return;
    const { turn, body } = this.makeTurn("User", "U", "var(--surface-2)", "var(--text)");
    const d = document.createElement("details");
    d.className = "prompt-block";
    const s = document.createElement("summary");
    s.innerHTML = '<span>View user prompt</span><span style="color: var(--dim);">▸</span>';
    const pre = document.createElement("pre");
    pre.textContent = text;
    d.appendChild(s); d.appendChild(pre);
    body.appendChild(d);
    this.feedTarget.appendChild(turn);
  }

  startAssistantTurn() {
    this.finalizeAssistant();
    // Thinking precedes the answer — collapse the open reasoning block so
    // the assistant text isn't buried under an expanded scratchpad.
    this.finalizeReasoning();
    const { turn, body } = this.makeTurn("Assistant", "A", "var(--accent)", "var(--accent-on)");
    const content = document.createElement("div");
    content.className = "md-content";
    body.appendChild(content);
    this.feedTarget.appendChild(turn);
    this.currentAssistantEl = content;
    this.currentAssistantRaw = "";
  }

  appendToAssistant(text) {
    if (!this.currentAssistantEl) this.startAssistantTurn();
    this.currentAssistantRaw += text;
    this.currentAssistantEl.textContent = this.currentAssistantRaw;
  }

  finalizeAssistant() {
    if (this.currentAssistantEl && this.currentAssistantRaw) {
      this.currentAssistantEl.innerHTML = renderMarkdown(this.currentAssistantRaw);
    }
    this.currentAssistantEl = null;
    this.currentAssistantRaw = "";
  }

  addToolCall(name, rawInput) {
    this.finalizeAssistant();
    const input = unwrapInput(rawInput);
    const preview = input.length > 96 ? input.slice(0, 96) + "…" : input;

    const { turn, body } = this.makeTurn("Tool", "T", "var(--surface-2)", "var(--accent)");

    const details = document.createElement("details");
    details.className = "tool-call";

    const summary = document.createElement("summary");
    summary.innerHTML =
      '<svg class="caret w-3 h-3" fill="none" stroke="currentColor" stroke-width="2.5" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" d="M9 5l7 7-7 7"/></svg>' +
      '<span class="tool-name">' + esc(name) + '</span>' +
      '<span class="tool-args">' + esc(preview) + '</span>' +
      '<span class="text-[11px]" data-size style="color: var(--dim);">…</span>';
    details.appendChild(summary);

    const section = document.createElement("div");
    section.className = "tool-section";

    const inputLabel = document.createElement("div");
    inputLabel.className = "tool-section-label";
    inputLabel.textContent = "Input";
    const inputPre = document.createElement("pre");
    inputPre.textContent = input;
    section.appendChild(inputLabel);
    section.appendChild(inputPre);

    const outputLabel = document.createElement("div");
    outputLabel.className = "tool-section-label";
    outputLabel.textContent = "Output";
    outputLabel.style.display = "none";
    const outputPre = document.createElement("pre");
    outputPre.style.display = "none";
    section.appendChild(outputLabel);
    section.appendChild(outputPre);

    details.appendChild(section);
    body.appendChild(details);
    this.feedTarget.appendChild(turn);

    return { summary, outputPre, outputLabel };
  }

  setToolResult(refs, output) {
    if (!refs) return;
    refs.outputPre.textContent = output;
    refs.outputPre.style.display = "";
    refs.outputLabel.style.display = "";
    const bytes = new TextEncoder().encode(output).length;
    const size = bytes > 1024 ? (bytes / 1024).toFixed(1) + " KB" : bytes + " B";
    const sizeSpan = refs.summary.querySelector("[data-size]");
    if (sizeSpan) sizeSpan.textContent = size;
    this.currentToolRefs = null;
  }

  removeWaiting() {
    if (this.hasWaitingTarget) this.waitingTarget.remove();
  }

  // applyUsage updates the live token/cost spans on the run header. The
  // spans are addressed by id (server-rendered in run.html); using
  // getElementById here keeps the template's ID contract stable without
  // demanding Stimulus targets for every span.
  applyUsage(p) {
    setElText("usage-in", fmtTokens(p.input_tokens));
    setElText("usage-out", fmtTokens(p.output_tokens));
    showEl("usage-info");
    if (p.cache_read_tokens) {
      showEl("usage-cache");
      setElText("usage-cache-val", fmtTokens(p.cache_read_tokens));
    }
    if (typeof p.cost_usd === "number") {
      showEl("cost-info");
      setElText("cost-val", fmtCost(p.cost_usd));
    }

    // Mirror the same numbers into the right rail, which is otherwise
    // server-rendered once and never moves during a live run.
    showEl("rail-tokens-row");
    setElText("rail-in", fmtTokens(p.input_tokens));
    setElText("rail-out", fmtTokens(p.output_tokens));
    if (p.cache_read_tokens) {
      showEl("rail-cache-row");
      setElText("rail-cache", fmtTokens(p.cache_read_tokens));
    }
    if (typeof p.cost_usd === "number") {
      showEl("rail-cost-row");
      setElText("rail-cost", fmtCost(p.cost_usd));
    }
  }

  // ── Right rail ────────────────────────────────────────────────────────────
  // Iterations and the tool breakdown are derived from the event stream rather
  // than pushed by the server. Counting starts at zero on connect and includes
  // the DB catch-up the socket replays first, so the totals match what the
  // server would have rendered — the SSR values are simply overwritten.

  bumpIterations() {
    this.iterationCount = (this.iterationCount || 0) + 1;
    showEl("rail-iterations-row");
    setElText("rail-iterations", String(this.iterationCount));
  }

  bumpToolCount(name) {
    if (!name) return;
    if (!this.toolCounts) this.toolCounts = new Map();
    this.toolCounts.set(name, (this.toolCounts.get(name) || 0) + 1);
    this.renderToolBreakdown();
  }

  renderToolBreakdown() {
    const list = document.getElementById("rail-tools");
    if (!list || !this.toolCounts) return;
    showEl("rail-tools-section");

    // Busiest tool first, matching the server-rendered ordering.
    const entries = [...this.toolCounts.entries()].sort((a, b) => b[1] - a[1]);
    list.replaceChildren(
      ...entries.map(([name, count]) => {
        const li = document.createElement("li");
        li.className = "flex justify-between";
        const n = document.createElement("span");
        n.className = "mono";
        n.style.color = "var(--muted)";
        n.textContent = name;
        const c = document.createElement("span");
        c.style.color = "var(--text)";
        c.textContent = String(count);
        li.appendChild(n);
        li.appendChild(c);
        return li;
      })
    );
  }

  markCompletedNow() {
    showEl("rail-completed-row");
    setElText("rail-completed", new Date().toLocaleString());
  }

  setStatus(text, classes) {
    this.statusBadgeTarget.textContent = text;
    this.statusBadgeTarget.className = "text-sm px-3 py-1 rounded-full " + classes;
  }
}
